import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { GenerateOffersResponse } from '@/app/services/panService/generateOffers'
import { useCustomerInfo } from '@/app/hooks/useCustomerInfo'
import { usePanMutations } from '@/app/hooks/usePanMutations'
import { usePanContext } from '@/app/contexts/PanContext'
import { useRegisterSupportContext } from "@/app/store/supportScreenContext";
import { PartnerType } from "@/app/utils/partners.enum";

export type LocationState = {
  offers: GenerateOffersResponse;
  selectedValue: number;
  selectedPrazo: number;
  prazoOptions: { label: string; value: number }[];
  customerId: string;
  preTransactionId: string;
};

export default function usePanSimulation() {
  const { state } = useLocation() as { state: LocationState }

  const { setCustomer, setProposal, setPreTransactionId } = usePanContext()

  const { generateOffersMutation } = usePanMutations()

  const [offers, setOffers] = useState<GenerateOffersResponse | null>(
    state?.offers,
  )

  const [selectedPrazo, setSelectedPrazo] = useState(state?.selectedPrazo)

  const prazoOptions = state?.prazoOptions
  const selectedValue = state?.selectedValue
  const customerId = state?.customerId
  const preTransactionId = state?.preTransactionId

  const [loadingOffers, setLoadingOffers] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const { data: customerInfo } = useCustomerInfo(customerId)

  const [selectedCondition, setSelectedCondition] = useState(
    offers?.conditions?.find((cond) => cond.sucesso) ?? null,
  )

  const handleChangePrazo = async (prazo: number) => {
    setSelectedPrazo(prazo)
    setErrorMessage(null)

    const payload = {
      customerId,
      valor: selectedValue,
      prazo,
    }

    try {
      setLoadingOffers(true)

      const response = await generateOffersMutation.mutateAsync({
        id: preTransactionId,
        payload,
      })

      setOffers(response)

      const condition = response?.conditions ?? []

      const conditionSuccess = condition.filter((c) => c.sucesso)

      if (conditionSuccess.length === 0) {
        const primeiraCondicaoComErro = condition.find((c) =>
          c.mensagem_erro?.trim(),
        )
        setErrorMessage(
          primeiraCondicaoComErro?.mensagem_erro ??
            'Nenhuma condição encontrada',
        )
        setSelectedCondition(null)
      } else {
        setSelectedCondition(conditionSuccess[0])
        setErrorMessage(null)
      }
    } catch (err: any) {
      setErrorMessage(err?.message ?? 'Erro ao buscar ofertas.')
      setSelectedCondition(null)
    } finally {
      setLoadingOffers(false)
    }
  }

  const handleMakeProposal = () => {
    if (!customerInfo || !selectedCondition) return

    setCustomer({
      cpf: customerInfo.cpf,
    })

    const { parcelas, ...operacoesCreditoSemParcelas } = selectedCondition

    setProposal({
      operacoes_credito: operacoesCreditoSemParcelas,
      customerId,
    })

    setPreTransactionId(preTransactionId)
  }

  useRegisterSupportContext('simulation.pan', {
    product: PartnerType.PAN,
    step: 'Ofertas Pan',
    data: {
      preTransactionId,
      customerName: customerInfo?.name,
      customerCpf: customerInfo?.cpf,
      requestedValue: selectedValue,
      installments: selectedPrazo,
      selectedCondition,
    },
  })

  return {
    offers,
    selectedPrazo,
    prazoOptions,
    selectedValue,
    customerInfo,
    selectedCondition,
    loadingOffers,
    errorMessage,
    setSelectedCondition,
    handleChangePrazo,
    handleMakeProposal,
  }
}
